<template>
  <b-nav-item-dropdown
    ref="seprobanDropdown"
    v-if="notification"
    class="dropdown-notification mr-75"
    menu-class="dropdown-menu-media dropdown-menu-right seproban-dropdown-menu"
    left
  >
    <template #button-content>
      <feather-icon
        :badge="alarms.length"
        badge-classes="bg-danger"
        class="text-body"
        icon="SendIcon"
        size="21"
        v-b-tooltip.hover.button.viewport="
          $t('seproban.images') + ' ' + $t('seproban.title')
        "
      />
    </template>

    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="scrollable-container media-list scroll-area"
      tagname="li"
    >
      <b-card
        v-for="alarm in alarms"
        :key="alarm.id_alarm"
        :id="'seproban-alarm-' + alarm.id_alarm"
        no-body
        class="my-1 mx-1"
      >
        <b-card-header class="p-0">
          <b-button
            block
            variant="light"
            class="text-left btn-sm"
            v-b-toggle="'alarm-collapse-' + alarm.id_alarm"
          >
            {{ $tc("alarm.title", 1) }} #{{ alarm.id_alarm }} -
            {{ alarm.unit_name }}
          </b-button>
        </b-card-header>

        <b-collapse
          :id="'alarm-collapse-' + alarm.id_alarm"
          accordion="alarms-accordion"
          :visible="focusedAlarmId === Number(alarm.id_alarm)"
        >
          <b-card-body>
            <send-to-seproban
              :alarm-id="alarm.id_alarm"
              usage-type="notification"
            />
          </b-card-body>
        </b-collapse>
      </b-card>
    </vue-perfect-scrollbar>
    <li class="dropdown-menu-footer">
      <b-button variant="primary" block @click="removeCompletedAlarms()"
        >{{ $t("notifications.clear_data_from_completed") }}
      </b-button>
    </li>
  </b-nav-item-dropdown>
</template>

<script>
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import store from "@/store";
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "@vue/composition-api";
import {
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BCollapse,
} from "bootstrap-vue";
import SendToSeproban from "@/views/components/GridView/widgets/SeprobanModal/SendToSeproban.vue";
import axios from "@/libs/axios";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BCollapse,
    VuePerfectScrollbar,
    SendToSeproban,
  },
  setup(_, { root }) {
    const user = store.getters["user/getUser"];
    const seprobanDropdown = ref(null);
    const focusedAlarmId = ref(null);

    const focusAlarm = async (alarmId) => {
      const normalizedAlarmId = Number(alarmId);
      const alarmExists = store.state.send_to_seproban.items.alarms.some(
        alarm => Number(alarm.id_alarm) === normalizedAlarmId
      );
      if (!alarmExists) return;

      focusedAlarmId.value = normalizedAlarmId;
      await nextTick();
      if (seprobanDropdown.value) seprobanDropdown.value.show();

      await nextTick();
      const alarmCard = document.getElementById(`seproban-alarm-${normalizedAlarmId}`);
      if (alarmCard) alarmCard.scrollIntoView({ block: "nearest" });
    };

    onMounted(() => {
      root.$on("seproban:focus-alarm", focusAlarm);
      if (store.state.send_to_seproban.items.alarms.length == 0) {
        loadAlarmsAndSnapshots();
      }
    });

    onBeforeUnmount(() => {
      root.$off("seproban:focus-alarm", focusAlarm);
    });

    const alarms = computed(() => store.state.send_to_seproban.items.alarms);


    const notification = computed(
      () => store.state.send_to_seproban.notification
    );

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    };

    const loadAlarmsAndSnapshots = async () => {
      try {
        const response = await axios.get("/v1/seproban/events", {
          params: {
            active: true,
            with_snapshots: true,
            max: 100,
          },
        });
        const alarms = response.data.data || [];

        if (!alarms.length) {
          return;
        }

        store.commit("send_to_seproban/setNotification", true);

        for (const alarm of alarms) {
          const alarmId = alarm.id_alarm;
          const unitName = alarm.unit?.description;
          const created = alarm.alarm_created_at;
          const totalImages = alarm.total_snapshots;
          store.commit("send_to_seproban/initAlarm", {
            alarmId: alarmId,
            seprobanAlarmId:
              alarm.id_seproban_alarm_event ??
              alarm.id_seproban_alarm ??
              alarm.seproban_alarm_id ??
              alarm.id,
            unitName: unitName,
            unit: alarm.unit,
            created: created,
            totalImages: totalImages,
            event: alarm,
          });
          
          setSnapshotsDownloadSeprobanWS(alarmId);
          setSnapshotsUploadSeprobanWS(alarmId);

          const cameras = alarm.cameras.map((cam) => ({ ...cam }));

          store.commit("send_to_seproban/setFilteredCameras", {
            alarmId: alarmId,
            cameras: cameras,
          });
        }
      } catch (error) {
        console.error(error);
      }
    };

    const setSnapshotsDownloadSeprobanWS = (alarmId) => {
      const id_alarm = alarmId;
      window.Echo.leave(`snapshots-download-seproban-${user.id}-${id_alarm}`);

      window.Echo.private(`snapshots-download-seproban-${user.id}-${id_alarm}`).listen(
        ".process",
        (data) => {
          //const hasImageFile = /\.(jpg|jpeg)$/i.test(data.image_url);
          if(data.image_url){
            store.commit("send_to_seproban/updateDownloadData", {
              alarmId: data.alarm_id,
              cameraId: data.camera_proxy_id,
              imageUrl: data.image_url,
              progress: data.progress,
              timestamp: data.timestamp
            });
          }
        }
      );
    };

    const setSnapshotsUploadSeprobanWS = (alarmId) => {
      const id_alarm = alarmId;
      window.Echo.leave(`snapshots-upload-seproban-${user.id}-${id_alarm}`)

      window.Echo.private(`snapshots-upload-seproban-${user.id}-${id_alarm}`).listen(
        ".process",
        (data) => {
          store.commit("send_to_seproban/updateUploadData", {
            alarmId: id_alarm,
            cameraId: data.camera_proxy_id,
            imageUrl: data.image_url,
            progress: data.progress,
            timestamp: data.sent_at || data.uploaded_at || Date.now(),
          });
          console.log("snapshots-upload-seproban", data);
        }
      );
    };

    const removeCompletedAlarms = () => {
      store.commit("send_to_seproban/removeAllCompletedAlarms");
    };

    return {
      alarms,
      notification,
      perfectScrollbarSettings,
      removeCompletedAlarms,
      seprobanDropdown,
      focusedAlarmId,
    };
  },
};
</script>

<style>
.dropdown-notification > .dropdown-menu.seproban-dropdown-menu {
  width: 700px !important;
  min-width: 700px !important;
  height: 80vh;
  max-height: 80vh;
  max-width: calc(100vw - 2rem) !important;
}

.dropdown-notification > .dropdown-menu.seproban-dropdown-menu.show {
  display: flex;
  flex-direction: column;
}

.seproban-dropdown-menu .scroll-area {
  flex: 1 1 auto;
  min-height: 0;
  max-height: none !important;
}

.seproban-dropdown-menu .dropdown-menu-footer {
  flex-shrink: 0;
}

@media (max-width: 732px) {
  .dropdown-notification > .dropdown-menu.seproban-dropdown-menu {
    width: calc(100vw - 2rem) !important;
    min-width: calc(100vw - 2rem) !important;
  }
}
</style>
